import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Hall } from '../../api/types';
import { DateField } from '../../components/DateField';
import { img, type PhotoKey } from '../../content/imagery';
import { OCCASIONS } from '../../content/occasions';
import { useT, type Key } from '../../i18n';
import { todayISO } from '../../lib/format';
import { cityGroups, prefersReducedMotion } from './data';
import { rich } from './ui';
import { placeName } from '../../lib/places';

/**
 * The pinned arch hero (reference home.js): a deep-teal stage with a slow
 * aurora and drifting gold dust. One photograph shows through an arch shaped
 * like the Aghosha mark; while the section is pinned, scrolling opens the arch
 * out to the full screen. Photos change with a curtain opening from the
 * centre, with progress bars and a pause button. The search panel sits at the
 * bottom. Everything waits for `ready` (set after the opening intro).
 */

const SLIDE_MS = 6500;

/** Editorial photographs (imagery.ts), not listings. */
const SLIDES: { key: PhotoKey; cap: Key }[] = [
  { key: 'weddingAisle', cap: 'hmSlideWeddings' },
  { key: 'ballroomGrand', cap: 'hmSlideBanquet' },
  { key: 'concertStage', cap: 'hmSlideConcerts' },
  { key: 'conventionHall', cap: 'hmSlideConferences' },
  { key: 'receptionTable', cap: 'hmSlideReceptions' },
];

const ease = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

export function Hero({ ready, halls }: { ready: boolean; halls: Hall[] | undefined }) {
  const { t } = useT();
  const hero = useRef<HTMLElement>(null);
  const pin = useRef<HTMLDivElement>(null);
  const foot = useRef<HTMLDivElement>(null);
  const dust = useRef<HTMLCanvasElement>(null);
  const [still] = useState(prefersReducedMotion);

  /* ------------------------------ slideshow ------------------------------ */
  const N = SLIDES.length;
  /** The showing slide, the one it replaced (kept under it while the curtain opens), and whether any change has happened. */
  const [show, setShow] = useState<{ cur: number; prev: number | null; changed: boolean }>({ cur: 0, prev: null, changed: false });
  const { cur, prev, changed } = show;
  const [paused, setPaused] = useState(still);
  /** Bumped when a bar is clicked, so the timer starts over. */
  const [nonce, setNonce] = useState(0);

  const go = useCallback((n: number) => {
    setShow((s) => (n === s.cur ? s : { cur: n, prev: s.cur, changed: true }));
  }, []);

  useEffect(() => {
    if (!ready || still || paused || N < 2) return;
    const id = window.setTimeout(() => go((cur + 1) % N), SLIDE_MS);
    return () => clearTimeout(id);
  }, [ready, still, paused, cur, nonce, go, N]);

  /* ----------------------- arch → full screen on scroll ----------------------- */
  useEffect(() => {
    const h = hero.current;
    const p = pin.current;
    if (!h || !p) return;
    const header = document.querySelector<HTMLElement>('.site-header');
    const layout = () => {
      const W = p.clientWidth;
      const H = p.clientHeight;
      const headH = header ? header.offsetHeight : 70;
      const footH = foot.current ? foot.current.offsetHeight : 0;
      const room = Math.max(H < 560 ? 120 : 220, H - headH - footH - (H < 560 ? 16 : 40));
      // wider arch on phones and tablets, slimmer on big screens
      const share = W <= 720 ? 0.6 : W <= 1080 ? 0.4 : 0.27;
      let aw = Math.min(Math.max(H < 560 ? 90 : 200, W * share), 420, W - 48);
      let ah = aw * 1.5;
      if (ah > room) {
        ah = room;
        aw = ah / 1.5;
      }
      const top0 = headH + (H - headH - footH - ah) / 2;
      const bottom0 = H - top0 - ah;
      const x0 = (W - aw) / 2;
      const r0 = aw / 2;
      const r = h.getBoundingClientRect();
      const span = Math.max(1, h.offsetHeight - H);
      const prog = still ? 0 : Math.max(0, Math.min(1, -r.top / span));
      const e = ease(prog);
      h.style.setProperty('--hp', prog.toFixed(3));
      h.style.setProperty('--at', `${(top0 * (1 - e)).toFixed(1)}px`);
      h.style.setProperty('--ab', `${(bottom0 * (1 - e)).toFixed(1)}px`);
      h.style.setProperty('--ax', `${(x0 * (1 - e)).toFixed(1)}px`);
      h.style.setProperty('--ar', `${(r0 * (1 - e)).toFixed(1)}px`);
      h.style.setProperty('--title-bottom', `${footH}px`);
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        layout();
        ticking = false;
      });
    };
    layout();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // The foot (search panel) can change height when fonts or data arrive.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onScroll) : null;
    if (ro && foot.current) ro.observe(foot.current);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      ro?.disconnect();
    };
  }, [still]);

  /* ------------------- gold dust (paused when off screen) ------------------- */
  useEffect(() => {
    const cv = dust.current;
    const h = hero.current;
    const ctx = cv?.getContext?.('2d');
    if (!cv || !h || !ctx) return;
    type P = { x: number; y: number; r: number; vy: number; sw: number; a: number; gold: boolean };
    let parts: P[] = [];
    let W = 0;
    let H = 0;
    let raf = 0;
    let visible = true;
    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = cv.clientWidth;
      H = cv.clientHeight;
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.round(Math.min(110, (W * H) / 14000));
      parts = Array.from({ length: n }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: Math.random() * 1.3 + 0.3,
        vy: -(Math.random() * 0.18 + 0.04),
        sw: Math.random() * Math.PI * 2,
        a: Math.random() * 0.55 + 0.15,
        gold: Math.random() < 0.55,
      }));
    };
    const draw = (move: boolean) => {
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        if (move) {
          p.y += p.vy;
          p.sw += 0.01;
          p.x += Math.sin(p.sw) * 0.12;
          if (p.y < -4) {
            p.y = H + 4;
            p.x = Math.random() * W;
          }
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold ? `rgba(226,185,98,${p.a})` : `rgba(243,236,225,${p.a * 0.7})`;
        ctx.fill();
      }
    };
    const loop = () => {
      draw(true);
      raf = visible ? requestAnimationFrame(loop) : 0;
    };
    const onResize = () => {
      size();
      if (still) draw(false);
    };
    size();
    window.addEventListener('resize', onResize);
    let io: IntersectionObserver | null = null;
    if (still) draw(false);
    else {
      if (typeof IntersectionObserver !== 'undefined') {
        io = new IntersectionObserver((en) => {
          visible = en[0].isIntersecting;
          if (visible && !raf) raf = requestAnimationFrame(loop);
        });
        io.observe(h);
      }
      raf = requestAnimationFrame(loop);
    }
    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [still]);

  const cls = ['hero', ready && 'is-ready', paused && 'is-paused'].filter(Boolean).join(' ');

  return (
    <section className={cls} aria-labelledby="hero-title" data-hero ref={hero} style={{ '--slide-ms': `${SLIDE_MS}ms` } as CSSProperties}>
      <div className="hero-pin" ref={pin}>
        <div className="hero-bg" aria-hidden="true">
          <div className="hero-aurora">
            <i />
            <i />
            <i />
            <i />
          </div>
          <canvas className="hero-dust" ref={dust} />
        </div>

        <div className="hero-media" aria-hidden="true">
          {SLIDES.map((s, i) => {
            const active = i === cur;
            const isPrev = i === prev && !active;
            const near = i === 0 || i === cur || i === (cur + 1) % N || isPrev;
            const c = ['slide', active && 'is-active', active && !changed && 'is-first', isPrev && 'is-prev']
              .filter(Boolean)
              .join(' ');
            return (
              <img
                key={s.key}
                className={c}
                data-fx={changed && !still && (active || isPrev) ? 'split' : undefined}
                src={img(s.key, 2000, { h: 1300, q: 75 })}
                srcSet={`${img(s.key, 1200, { h: 780, q: 75 })} 1200w, ${img(s.key, 2000, { h: 1300, q: 75 })} 2000w`}
                sizes="100vw"
                alt=""
                loading={near ? 'eager' : 'lazy'}
                decoding="async"
                {...(i === 0 ? { fetchpriority: 'high' } : {})}
              />
            );
          })}
        </div>
        <div className="hero-frame" aria-hidden="true" />

        <div className="hero-title">
          <h1 id="hero-title">
            <span className="ln">
              <span style={{ '--d': '.55s' } as CSSProperties}>{t('hmHeroLine1')}</span>
            </span>{' '}
            <span className="ln">
              <span style={{ '--d': '.7s' } as CSSProperties}>{rich(t('hmHeroLine2'), 'em')}</span>
            </span>
          </h1>
        </div>

        <div className="hero-foot" ref={foot}>
          <div className="wrap">
            <div data-in="" style={{ '--d': '1.05s' } as CSSProperties}>
              <HeroSearch halls={halls} />
            </div>
            <div className="hero-meta" data-in="" style={{ '--d': '1.2s' } as CSSProperties}>
              <div className="hero-now">
                <span className="hn-bars">
                  {SLIDES.map((s, k) => (
                    <button
                      type="button"
                      key={s.key}
                      className={k === cur && ready ? 'is-active' : k < cur ? 'is-done' : undefined}
                      aria-current={k === cur ? 'true' : 'false'}
                      aria-label={t('hmSlideShow', { n: k + 1, total: N, caption: t(s.cap) })}
                      onClick={() => {
                        go(k);
                        setNonce((x) => x + 1);
                      }}
                    />
                  ))}
                </span>
              </div>
              <button
                type="button"
                className="hero-play"
                aria-pressed={paused}
                aria-label={paused ? t('hmSlidePlay') : t('hmSlidePause')}
                onClick={() => setPaused((p) => !p)}
              >
                <span className="sr">{paused ? t('hmPlay') : t('hmPause')}</span>
                <span>
                  <svg className="ic-pause" viewBox="0 0 12 12" aria-hidden="true">
                    <rect x="2" y="1.5" width="2.6" height="9" rx=".6" />
                    <rect x="7.4" y="1.5" width="2.6" height="9" rx=".6" />
                  </svg>
                  <svg className="ic-play" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M3 1.6v8.8a.6.6 0 0 0 .9.5l7-4.4a.6.6 0 0 0 0-1L3.9 1.1a.6.6 0 0 0-.9.5z" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- search --------------------------------- */

/**
 * The hero search: Location and Event type (custom selects), Date and Guests.
 * Submits to /venues with the same query the venues page reads:
 * location, occasion, date, minCap.
 */
function HeroSearch({ halls }: { halls: Hall[] | undefined }) {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [occasion, setOccasion] = useState('');
  const [date, setDate] = useState('');
  const [guests, setGuests] = useState('');
  const [busy, setBusy] = useState(false);

  const cityOptions = useMemo(
    () => [
      { value: '', label: t('hmSearchAnywhere') },
      ...cityGroups(halls ?? [])
        .map((g) => g.city)
        .sort((a, b) => a.localeCompare(b))
        .map((c) => ({ value: c, label: placeName(c, lang) })),
    ],
    [halls, t, lang],
  );
  const occOptions = useMemo(
    () => [{ value: '', label: t('hmSearchAnyEvent') }, ...OCCASIONS.map((o) => ({ value: o.slug, label: t(o.title) }))],
    [t],
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (location) p.set('location', location);
    if (occasion) p.set('occasion', occasion);
    if (date) p.set('date', date);
    const n = Math.floor(Number(guests));
    if (n > 0) p.set('minCap', String(n));
    const qs = p.toString();
    setBusy(true);
    navigate(qs ? `/venues?${qs}` : '/venues');
  };

  return (
    <form className="search-panel" role="search" aria-label={t('hmSearchLabel')} onSubmit={submit}>
      <HeroSelect icon="ri-map-pin-2-line" label={t('hmSearchLocation')} value={location} options={cityOptions} onChange={setLocation} />
      <HeroSelect icon="ri-sparkling-2-line" label={t('hmSearchEvent')} value={occasion} options={occOptions} onChange={setOccasion} />
      <div className="field">
        <span className="field-label">
          <i className="ri-calendar-event-line i" aria-hidden="true" />
          {t('hmSearchDate')}
        </span>
        <DateField value={date} onChange={setDate} min={todayISO()} placeholder={t('hmSearchDatePh')} />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="hm-guests">
          <i className="ri-group-line i" aria-hidden="true" />
          {t('hmSearchGuests')}
        </label>
        <input
          id="hm-guests"
          className="bare-input"
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          placeholder={t('hmSearchGuestsPh')}
          value={guests}
          onChange={(e) => setGuests(e.target.value)}
        />
      </div>
      <button type="submit" className={`btn btn-primary search-btn${busy ? ' is-loading' : ''}`}>
        <i className="ri-search-line i" aria-hidden="true" />
        {t('hmSearchGo')}
      </button>
    </form>
  );
}

/** The design's custom select (.select / .select-btn / .drop-panel) as a listbox. */
function HeroSelect({
  icon,
  label,
  value,
  options,
  onChange,
}: {
  icon: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const opts = useRef<(HTMLButtonElement | null)[]>([]);
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const sel = Math.max(0, options.findIndex((o) => o.value === value));
    opts.current[sel]?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
    // focus only when opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    btn.current?.focus();
  };

  const onOptKey = (e: React.KeyboardEvent, i: number) => {
    const n = options.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      opts.current[(i + 1) % n]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      opts.current[(i - 1 + n) % n]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      opts.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      opts.current[n - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btn.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="field">
      <span className="field-label" id={`${id}-l`}>
        <i className={`${icon} i`} aria-hidden="true" />
        {label}
      </span>
      <div className={`select${open ? ' is-open' : ''}`} ref={wrap}>
        <button
          type="button"
          ref={btn}
          className="select-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${id}-l ${id}-v`}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span id={`${id}-v`}>{current?.label}</span>
          <i className="ri-arrow-down-s-line i" aria-hidden="true" />
        </button>
        <div className="drop-panel" role="listbox" aria-labelledby={`${id}-l`}>
          {options.map((o, i) => (
            <button
              type="button"
              role="option"
              key={o.value || '_any'}
              ref={(el) => {
                opts.current[i] = el;
              }}
              tabIndex={-1}
              aria-selected={o.value === value}
              onClick={() => pick(o.value)}
              onKeyDown={(e) => onOptKey(e, i)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
